import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Button, Select } from '../../../design-system';
import {
  SwHeightResult,
  SwHeightSample,
  SwHeightZone,
  WellLogs
} from '../../../@types/swHeight/types';
import LogTrack, { MARGIN_BOTTOM, MARGIN_TOP } from './LogTrack';
import { useFillHeights } from './fillHeight';
import {
  DEPTH_SCALE_OPTIONS,
  DepthScaleMode,
  scaledPlotHeight
} from './depthScale';
import TrackHeader from './TrackHeader';
import { CrosshairLine, CrosshairTooltip } from './CrosshairOverlay';
import { createDepthCrosshairStore, offsetToDepth } from './depthCrosshair';
import {
  DepthSeriesPoint,
  TrackCurveHeader,
  buildRockTypeBands,
  depthRange,
  mdToReference,
  niceBound,
  rockTypeColor,
  toDepthSeries,
  valueRange
} from './logTracks';
import { useSyncedDepthExtremes } from './useSyncedDepthExtremes';
import { CURVE_COLORS, CURVE_UNITS } from './curveColors';
import styles from './LogViewer.module.css';

export type DepthReference = 'md' | 'tvdss';

export interface LogViewerProps {
  logs: WellLogs;
  result: SwHeightResult | null;
  zones: SwHeightZone[];
  samples?: SwHeightSample[];
  depthReference: DepthReference;
  /** Fixed pixel height per track. Omit to fill the space left on the page. */
  height?: number;
  /**
   * Core/RCAL rows, used only for their measured lab Sw on the saturation
   * track. Sparse by nature, so drawn as points and never interpolated into
   * a line. They carry their own depth curves, so no mapping onto the log
   * axis is needed.
   */
  coreMeasured?: WellLogs | null;
}

type LegendShape = 'line' | 'area' | 'point';

const SAMPLE_COLOR = CURVE_COLORS.sample;
const CORE_COLOR = CURVE_COLORS.swCore;

export const MEASURED_SERIES_NAME = 'SWT (log)';
export const CORE_SERIES_NAME = 'Lab Sw (core)';
export const MODEL_SERIES_NAME = 'Sw (model)';
export const MODEL_BY_ROCK_TYPE_SERIES_NAME = 'Sw (model, per rock type)';
const SW_TRACK_TITLE = 'Sw';

const MODEL_COLOR = CURVE_COLORS.swModel;
const MODEL_BY_ROCK_TYPE_COLOR = CURVE_COLORS.swModelByRockType;
const MEASURED_COLOR = CURVE_COLORS.swMeasured;

const sampleScatter = (
  points: { depth: number; value: number; label: string }[],
  options: Partial<Highcharts.SeriesScatterOptions> = {}
): Highcharts.SeriesOptionsType =>
  ({
    type: 'scatter',
    name: 'Samples',
    color: SAMPLE_COLOR,
    // The track sets `marker.enabled: false` and `lineWidth: 1` chart-wide
    // through plotOptions.series, which Highcharts merges after the scatter
    // type's own defaults (highcharts.src.js: merge(typeOptions,
    // plotOptions.series, ...)). Both have to be overridden here or the
    // points render as a connecting line with no markers.
    lineWidth: 0,
    marker: {
      enabled: true,
      symbol: 'circle',
      radius: 4,
      lineWidth: 1,
      lineColor: '#ffffff'
    },
    zIndex: 5,
    data: points.map(point => ({
      x: point.depth,
      y: point.value,
      name: point.label
    })),
    tooltip: {
      pointFormat: '<b>{point.name}</b><br/>depth: {point.x}, {point.y}'
    },
    ...options
  } as Highcharts.SeriesOptionsType);

/*
 * Only a fallback now: the tracks measure the space left below them and fill
 * it, so on a tall screen they are much taller than this. It stands in wherever
 * that measurement cannot be taken -- a hidden tab panel, jsdom.
 */
const TRACK_HEIGHT_DEFAULT = 640;
// The depth track is almost all margin: its content *is* the axis. Wide enough
// for LogTrack's label gutter plus a real strip of plot -- a couple of pixels
// would leave Highcharts nothing to lay the axis out in.
const DEPTH_TRACK_WIDTH = 88;

/**
 * The 1px border `.bodyCell` draws above and below each chart.
 *
 * Small enough to look like it could not matter, and it is the difference
 * between the tracks fitting the scroller at full scale and the scroller raising
 * a vertical scrollbar for two pixels of border.
 */
const TRACK_CELL_FRAME = 2;

const line = (
  name: string,
  color: string,
  data: [number, number | null][]
): Highcharts.SeriesOptionsType => ({
  type: 'line',
  name,
  color,
  data
});

/**
 * Petrel-style multi-track depth log viewer: one inverted Highcharts chart
 * per track, all sharing a synced depth window so zoom/pan on any track
 * realigns the whole display.
 */
const LogViewerComponent: React.FC<LogViewerProps> = ({
  logs,
  result,
  zones,
  samples = [],
  depthReference,
  height,
  coreMeasured = null
}) => {
  const depth = useMemo(
    () => logs.curves[depthReference] ?? logs.curves.md ?? [],
    [logs, depthReference]
  );

  const coreDepth = useMemo(
    () =>
      coreMeasured
        ? coreMeasured.curves[depthReference] ?? coreMeasured.curves.md ?? []
        : [],
    [coreMeasured, depthReference]
  );

  // Sample depths are MD; map them onto the displayed reference.
  const positionedSamples = useMemo(() => {
    const md = logs.curves.md ?? [];
    return samples
      .map(sample => ({
        ...sample,
        displayDepth:
          depthReference === 'md'
            ? sample.depth
            : mdToReference(md, depth, sample.depth)
      }))
      .filter(
        (sample): sample is SwHeightSample & { displayDepth: number } =>
          sample.displayDepth !== null && sample.displayDepth !== undefined
      );
  }, [samples, logs, depth, depthReference]);

  // One store per viewer. Held outside React state so a pointer move cannot
  // re-render the five charts -- only the line and the readouts subscribe.
  const crosshair = useMemo(() => createDepthCrosshairStore(), []);

  // The plot box every track shares, from the same constants LogTrack applies
  // to each spec. Not measured: a `keepMounted` panel measures 0 while hidden,
  // and jsdom measures nothing at all.
  const bodyRowRef = useRef<HTMLDivElement>(null);
  const headerRowRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  /*
   * Fill the page vertically. An explicit `height` still wins -- a caller that
   * states a size means it -- but the page does not pass one, so on screen the
   * tracks run from under the headers to the bottom of the page. Track widths
   * are untouched: depth is the y axis, so a taller track shows more of the
   * well at the same horizontal scale rather than stretching anything.
   */
  const fill = useFillHeights(
    scrollerRef,
    headerRowRef,
    TRACK_HEIGHT_DEFAULT,
    TRACK_CELL_FRAME
  );

  const [scaleMode, setScaleMode] = useState<DepthScaleMode>('full');

  const fullRange = useMemo(() => depthRange(depth), [depth]);

  /**
   * How far from a sample the pointer may be and still read its value.
   *
   * Half the sample spacing, so between two samples the tooltip picks the
   * nearer one and never invents a value for a gap. Falls back generously when
   * there is too little data to measure a spacing.
   */
  const depthTolerance = useMemo(() => {
    const valid = depth.filter(
      (value): value is number => value !== null && value !== undefined
    );
    if (valid.length < 2) return Infinity;
    return Math.abs(valid[1] - valid[0]) / 2 || Infinity;
  }, [depth]);
  const { extremes, isZoomed, handleAfterSetExtremes, reset } =
    useSyncedDepthExtremes(fullRange.min, fullRange.max);

  /*
   * At a fixed scale the depth window dictates the height and the scroller
   * clips it; at 'full' the page dictates the height and the whole window fits.
   * Measured against the *visible* window, so a drag-zoom at 1:500 shortens the
   * track instead of quietly re-scaling it.
   *
   * The scale sizes the plot area, so the track's own margins are added back on
   * top -- they carry no depth.
   */
  const scaledHeight = useMemo(() => {
    const plotHeight = scaledPlotHeight(
      scaleMode,
      Math.abs(extremes.max - extremes.min)
    );
    return plotHeight === null ? null : plotHeight + MARGIN_TOP + MARGIN_BOTTOM;
  }, [scaleMode, extremes.min, extremes.max]);

  const trackHeight = scaledHeight ?? height ?? fill.plot;

  // The plot box every track shares, from the same constants LogTrack applies
  // to each spec. Not measured: a `keepMounted` panel measures 0 while hidden,
  // and jsdom measures nothing at all.
  const plot = useMemo(
    () => ({
      top: MARGIN_TOP,
      height: trackHeight - MARGIN_TOP - MARGIN_BOTTOM
    }),
    [trackHeight]
  );

  // Drag-zooming a track sets `userMin`/`userMax` on *that* chart's axis,
  // and Highcharts gives those precedence over the `min`/`max` we pass in
  // (highcharts.src.js: `hardMin = pick(axis.userMin, options.min)`). So a
  // prop change can never widen the chart that was zoomed -- it stayed
  // zoomed while every other track reset. Push the shared window to every
  // chart through setExtremes instead, which is what clears them.
  const charts = useRef(new Map<string, HighchartsReact.RefObject>());
  const registrars = useRef(
    new Map<string, (instance: HighchartsReact.RefObject | null) => void>()
  );

  // One stable callback per track: an inline closure would be a new ref
  // every render, making React detach and reattach each chart.
  const registerChart = useCallback((key: string) => {
    let registrar = registrars.current.get(key);
    if (!registrar) {
      registrar = (instance: HighchartsReact.RefObject | null) => {
        if (instance) {
          charts.current.set(key, instance);
        } else {
          charts.current.delete(key);
        }
      };
      registrars.current.set(key, registrar);
    }
    return registrar;
  }, []);

  useEffect(() => {
    charts.current.forEach(instance => {
      const axis = instance?.chart?.xAxis?.[0];
      if (!axis) return;
      // `undefined` clears the user zoom so the axis falls back to the
      // full range. setExtremes fires afterSetExtremes without a zoom/pan
      // trigger, so this cannot feed back into the shared window.
      axis.setExtremes(
        isZoomed ? extremes.min : undefined,
        isZoomed ? extremes.max : undefined,
        true,
        false
      );
    });
  }, [extremes.min, extremes.max, isZoomed]);

  const { tracks, legendItems } = useMemo(() => {
    const curves = logs.curves;
    const legend: { name: string; color: string; shape: LegendShape }[] = [];
    const built: {
      key: string;
      title: string;
      /** 'depth' has no value scale; 'category' is scaled 0-1 by convention. */
      kind: 'depth' | 'curve' | 'category';
      series: Highcharts.SeriesOptionsType[];
      /** One header pill per curve, in series order. */
      curves: TrackCurveHeader[];
      /**
       * The points behind each pill, for the crosshair readout. Kept beside
       * the pill rather than read back off the chart: Highcharts crops
       * `series.points` past `cropThreshold` (300, and there are ~621 rows)
       * and `boost` removes it entirely.
       */
      readouts: { curve: TrackCurveHeader; points: DepthSeriesPoint[] }[];
      valueAxis?: {
        min?: number;
        max?: number;
        type?: 'linear' | 'logarithmic';
      };
      depthPlotBands?: Highcharts.XAxisPlotBandsOptions[];
      boost?: boolean;
      width?: number;
    }[] = [];

    /**
     * A fixed scale over the *whole* log, never the visible window.
     *
     * Highcharts would otherwise rescale the value axis to whatever the depth
     * zoom leaves visible, and a header stating "0 - 3.96" would then be
     * describing a plot scaled to something else. A wireline print fixes its
     * track scales for the same reason: the number on the header has to stay
     * true. Rounded outward so it never clips real data.
     */
    const fixedScale = (points: DepthSeriesPoint[]) => {
      const { min, max } = valueRange(points);
      if (min === null || max === null) return undefined;
      if (min === max) return { min: niceBound(min, 'down'), max: min + 1 };
      return { min: niceBound(min, 'down'), max: niceBound(max, 'up') };
    };

    // GR/CALI/RHOB/PHIE tracks are intentionally hidden for now —
    // the calibration view focuses on RQI, FZI, RT and SWT/Sw.
    const samplePoints = (
      getValue: (sample: SwHeightSample) => number | null
    ) =>
      positionedSamples
        .map(sample => ({
          depth: sample.displayDepth,
          value: getValue(sample),
          label: sample.sample_code ?? sample.sample_uid
        }))
        .filter(
          (point): point is { depth: number; value: number; label: string } =>
            point.value !== null && point.value !== undefined
        );

    // Core plug measurements: discrete points, never a line.
    const coreScatter = (
      values: (number | null)[] | undefined
    ): Highcharts.SeriesOptionsType[] => {
      if (!coreMeasured || !values) {
        return [];
      }
      const data = toDepthSeries(coreDepth, values).filter(
        ([, value]) => value !== null
      );
      if (data.length === 0) {
        return [];
      }
      return [
        {
          type: 'scatter',
          name: CORE_SERIES_NAME,
          color: CORE_COLOR,
          // Points only: see the note on sampleScatter -- the track's
          // chart-wide plotOptions would otherwise draw a line through them
          // and hide the markers.
          lineWidth: 0,
          marker: {
            enabled: true,
            symbol: 'diamond',
            radius: 3.5,
            lineWidth: 1,
            lineColor: '#ffffff'
          },
          zIndex: 4,
          data
        } as Highcharts.SeriesOptionsType
      ];
    };

    if (positionedSamples.length) {
      legend.push({ name: 'Samples', color: SAMPLE_COLOR, shape: 'point' });
    }

    // A track of its own for the depth axis, first on the left. It names the
    // reference at the top like every other track names its curve, which is
    // what let the rotated axis title -- and the wide gutter that had to fit
    // it -- go away.
    built.push({
      key: 'depth',
      title: depthReference.toUpperCase(),
      kind: 'depth',
      // No pill: the ruler's scale *is* the depth window, which the axis
      // labels already state.
      curves: [],
      readouts: [],
      // One placeholder series carrying no data, exactly as the rock-type
      // track does. Highcharts draws no axes at all for a chart with *zero*
      // series, so an empty `series: []` renders an empty box -- the axis is
      // this track's entire content, so it has to have something to hang off.
      series: [
        {
          type: 'line',
          name: 'Depth',
          data: [],
          showInLegend: false,
          enableMouseTracking: false
        } as Highcharts.SeriesOptionsType
      ],
      valueAxis: { min: 0, max: 1 },
      width: DEPTH_TRACK_WIDTH
    });

    if (result?.rqi?.some(value => value !== null)) {
      const rqiSamples = samplePoints(sample => sample.rqi);
      const rqiPoints = toDepthSeries(depth, result.rqi);
      const rqiScale = fixedScale(rqiPoints);
      const rqiCurves: TrackCurveHeader[] = [
        {
          id: 'rqi',
          label: 'RQI',
          color: CURVE_COLORS.rqi,
          shape: 'line',
          unit: CURVE_UNITS.rqi,
          min: rqiScale?.min ?? null,
          max: rqiScale?.max ?? null
        }
      ];
      built.push({
        key: 'rqi',
        title: 'RQI',
        kind: 'curve',
        series: [
          line('RQI', CURVE_COLORS.rqi, rqiPoints),
          ...(rqiSamples.length ? [sampleScatter(rqiSamples)] : [])
        ],
        curves: rqiCurves,
        readouts: [{ curve: rqiCurves[0], points: rqiPoints }],
        valueAxis: rqiScale,
        boost: true
      });
    }
    if (result?.fzi?.some(value => value !== null)) {
      const fziSamples = samplePoints(sample => sample.fzi);
      const fziPoints = toDepthSeries(depth, result.fzi);
      const fziRange = valueRange(fziPoints);
      // A logarithmic axis needs a strictly positive domain, and FZI can
      // legitimately be tiny, so clamp the floor instead of letting
      // Highcharts drop the axis.
      const fziMin =
        fziRange.min !== null && fziRange.min > 0 ? fziRange.min : 0.01;
      const fziMax = fziRange.max !== null && fziRange.max > fziMin
        ? fziRange.max
        : fziMin * 100;
      const fziCurves: TrackCurveHeader[] = [
        {
          id: 'fzi',
          label: 'FZI',
          color: CURVE_COLORS.fzi,
          shape: 'line',
          unit: CURVE_UNITS.fzi,
          min: fziMin,
          max: fziMax
        }
      ];
      built.push({
        key: 'fzi',
        title: 'FZI',
        kind: 'curve',
        series: [
          line('FZI', CURVE_COLORS.fzi, fziPoints),
          ...(fziSamples.length ? [sampleScatter(fziSamples)] : [])
        ],
        curves: fziCurves,
        readouts: [{ curve: fziCurves[0], points: fziPoints }],
        valueAxis: { type: 'logarithmic', min: fziMin, max: fziMax },
        boost: true
      });
    }
    if (result?.rock_type?.some(value => value !== null)) {
      const rtSamples = positionedSamples.filter(
        sample => sample.rock_type !== null
      );
      built.push({
        key: 'rock-type',
        title: 'RT',
        kind: 'category',
        // A bin index, not a measurement: the colour key below the pill says
        // what the bands mean, so a 0-1 scale would be meaningless here.
        curves: [
          {
            id: 'rock-type',
            label: 'Rock Type',
            color: CURVE_COLORS.rockType,
            shape: 'line',
            min: null,
            max: null
          }
        ],
        // The bands say which bin; a number would add nothing.
        readouts: [],
        series: [
          {
            type: 'line',
            name: 'Rock Type',
            data: [],
            showInLegend: false,
            enableMouseTracking: false
          } as Highcharts.SeriesOptionsType,
          ...(rtSamples.length
            ? [
                {
                  ...sampleScatter(
                    rtSamples.map(sample => ({
                      depth: sample.displayDepth,
                      value: 0.5,
                      label: `${
                        sample.sample_code ?? sample.sample_uid
                      } — RT ${sample.rock_type}`
                    }))
                  ),
                  // Color each marker by its own rock type.
                  data: rtSamples.map(sample => ({
                    x: sample.displayDepth,
                    y: 0.5,
                    name: `${
                      sample.sample_code ?? sample.sample_uid
                    } — RT ${sample.rock_type}`,
                    color: rockTypeColor(sample.rock_type as number, zones)
                  })),
                  tooltip: { pointFormat: '<b>{point.name}</b>' }
                } as Highcharts.SeriesOptionsType
              ]
            : [])
        ],
        valueAxis: { min: 0, max: 1 },
        depthPlotBands: buildRockTypeBands(depth, result.rock_type, zones),
        width: 90
      });
    }
    const swSeries: Highcharts.SeriesOptionsType[] = [];
    // Header pills, built in step with the series so their order matches.
    // Every saturation shares the 0-1 axis -- that shared scale is exactly
    // what makes the measured and modelled curves comparable, so each pill
    // states it rather than assuming the reader infers it.
    const swCurves: TrackCurveHeader[] = [];
    const swReadouts: {
      curve: TrackCurveHeader;
      points: DepthSeriesPoint[];
    }[] = [];
    const swPill = (
      id: string,
      label: string,
      color: string,
      shape: 'line' | 'points'
    ): TrackCurveHeader => ({
      id,
      label,
      color,
      shape,
      unit: CURVE_UNITS.sw,
      min: 0,
      max: 1
    });

    if (result?.sw?.some(value => value !== null)) {
      // Plotted as a plain line for now. It was an area filled to Sw = 1,
      // which shades the hydrocarbon side (1 - Sw) rather than Sw itself --
      // easy to misread against a legend block labelled "Sw (model)".
      // How to present that is being decided separately.
      legend.push({
        name: MODEL_SERIES_NAME,
        color: MODEL_COLOR,
        shape: 'line'
      });
      swSeries.push(
        line(MODEL_SERIES_NAME, MODEL_COLOR, toDepthSeries(depth, result.sw))
      );
      const pill = swPill('sw-model', MODEL_SERIES_NAME, MODEL_COLOR, 'line');
      swCurves.push(pill);
      swReadouts.push({ curve: pill, points: toDepthSeries(depth, result.sw) });
    }
    // The same model with one parameter set per rock type. It steps at bin
    // boundaries where the continuous curve does not, and has gaps over
    // rock types the correlation never populated -- both are the point of
    // showing it beside the other, so no smoothing or gap filling.
    if (result?.sw_by_rock_type?.some(value => value !== null)) {
      legend.push({
        name: MODEL_BY_ROCK_TYPE_SERIES_NAME,
        color: MODEL_BY_ROCK_TYPE_COLOR,
        shape: 'line'
      });
      swSeries.push(
        line(
          MODEL_BY_ROCK_TYPE_SERIES_NAME,
          MODEL_BY_ROCK_TYPE_COLOR,
          toDepthSeries(depth, result.sw_by_rock_type)
        )
      );
      const pill = swPill(
        'sw-by-rock-type',
        MODEL_BY_ROCK_TYPE_SERIES_NAME,
        MODEL_BY_ROCK_TYPE_COLOR,
        'line'
      );
      swCurves.push(pill);
      swReadouts.push({
        curve: pill,
        points: toDepthSeries(depth, result.sw_by_rock_type)
      });
    }
    if (curves.swt) {
      legend.push({
        name: MEASURED_SERIES_NAME,
        color: MEASURED_COLOR,
        shape: 'line'
      });
      swSeries.push({
        ...line(
          MEASURED_SERIES_NAME,
          MEASURED_COLOR,
          toDepthSeries(depth, curves.swt)
        ),
        zIndex: 2
      } as Highcharts.SeriesOptionsType);
      const pill = swPill('swt', MEASURED_SERIES_NAME, MEASURED_COLOR, 'line');
      swCurves.push(pill);
      swReadouts.push({
        curve: pill,
        points: toDepthSeries(depth, curves.swt)
      });
    }

    const corePoints = coreScatter(coreMeasured?.curves?.swt);
    if (corePoints.length) {
      legend.push({
        name: CORE_SERIES_NAME,
        color: CORE_COLOR,
        shape: 'point'
      });
    }
    if (corePoints.length) {
      const pill = swPill('sw-core', CORE_SERIES_NAME, CORE_COLOR, 'points');
      swCurves.push(pill);
      // Against the core dataset's *own* depths -- the plugs are sampled
      // independently of the log rows, which is why they are points.
      swReadouts.push({
        curve: pill,
        points: toDepthSeries(
          coreDepth,
          coreMeasured?.curves?.swt
        ).filter(([, value]) => value !== null)
      });
    }
    swSeries.push(...corePoints);
    if (swSeries.length > 0) {
      built.push({
        key: 'sw',
        title: SW_TRACK_TITLE,
        kind: 'curve',
        series: swSeries,
        curves: swCurves,
        readouts: swReadouts,
        valueAxis: { min: 0, max: 1 },
        // Wider than the index tracks: it carries two model curves, the
        // measured log and the core points on one 0-1 axis.
        width: 280
      });
    }
    return { tracks: built, legendItems: legend };
  }, [
    logs,
    result,
    zones,
    depth,
    // The depth track is titled after the reference, so switching TVDSS/MD
    // has to rebuild it.
    depthReference,
    positionedSamples,
    coreMeasured,
    coreDepth
  ]);

  /**
   * Every track's curves, flattened for the one tooltip. Tracks with nothing to
   * report drop out -- the ruler has no curve, and the rock-type bands already
   * name their bin.
   */
  const readoutGroups = useMemo(
    () =>
      tracks
        .filter(track => track.readouts.length > 0)
        .map(track => ({
          trackKey: track.key,
          trackTitle: track.title,
          curves: track.readouts
        })),
    [tracks]
  );

  if (depth.length === 0) {
    return null;
  }

  return (
    <div className={styles.viewer} data-testid="sw-height-log-viewer">
      <div className={styles.toolbar} data-testid="log-viewer-toolbar">
        {/*
          * An external label rather than the Select's own: `label` stacks above
          * the control, and this toolbar sits between the tab strip and the
          * first curve, where every row of chrome is a row of well nobody can
          * see. `htmlFor` targets the id Select puts on its trigger, so it is
          * still a labelled control.
          */}
        <div className={styles.control}>
          <label className={styles.controlLabel} htmlFor="log-depth-scale">
            Scale
          </label>
          <Select
            id="log-depth-scale"
            size="sm"
            options={DEPTH_SCALE_OPTIONS}
            value={scaleMode}
            onChange={value => setScaleMode(value as DepthScaleMode)}
          />
        </div>
        {isZoomed && (
          <Button variant="secondary" onClick={reset}>
            Reset zoom
          </Button>
        )}
      </div>
      {/*
        * One scroller for both rows. Separate horizontal scrollers would let
        * the headers slide out of line with their plots the moment the user
        * scrolls sideways, and header-over-plot alignment is the whole point.
        */}
      <div
        className={styles.scroller}
        data-testid="log-tracks-scroller"
        ref={scrollerRef}
        /*
         * Bounded only when the tracks are taller than the page, which is what a
         * fixed scale is for: then the scroller clips them and scrolls. At full
         * scale, or at a scale coarse enough to fit, it stays auto -- the tracks
         * are already exactly as tall as the space, and a fixed height would only
         * risk a scrollbar over a rounding error or hold empty space below a
         * short plot. Inline because it is a measurement.
         */
        style={
          trackHeight > fill.plot ? { height: fill.viewport } : undefined
        }
      >
        {/*
          * Both rows iterate the same ordered tracks and apply the same
          * `track.width` inline, so a header sits over its own plot by
          * construction rather than by measurement.
          */}
        <div
          className={styles.headerRow}
          data-testid="log-header-row"
          ref={headerRowRef}
        >
          {tracks.map(track => (
            <div
              key={track.key}
              className={styles.headerCell}
              data-testid={`log-track-header-${track.key}`}
              style={{ width: track.width ?? 170 }}
            >
              <TrackHeader
                trackKey={track.key}
                title={track.title}
                kind={track.kind}
                curves={track.curves}
              />
            </div>
          ))}
        </div>
        {/*
          * Handlers on the row, not on each cell, so moving through the gaps
          * between tracks still drives the crosshair. Highcharts does not stop
          * propagation on mousemove, so drag-to-zoom still works.
          *
          * Mouse rather than pointer events: hover is a mouse concept (a touch
          * has no hover state), and jsdom implements MouseEvent but not
          * PointerEvent -- with pointer events the coordinates never arrive in
          * tests, so the crosshair silently positioned itself at NaN.
          */}
        <div
          className={styles.bodyRow}
          data-testid="log-body-row"
          ref={bodyRowRef}
          onMouseMove={event => {
            const rect = event.currentTarget.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;
            /*
             * The clip edges are the *scroller's*, not the row's, expressed in
             * row coordinates. At a fixed scale the row is several screens tall
             * and scrolled sideways, so its own edges are nowhere near the ones
             * that would actually cut the tooltip off.
             */
            const view = scrollerRef.current?.getBoundingClientRect();
            crosshair.set({
              depth: offsetToDepth(y, extremes, plot),
              x,
              y,
              // Handed along so the tooltip can flip away from an edge without
              // measuring anything itself.
              clipRight: view ? x + (view.right - event.clientX) : rect.width,
              clipBottom: view ? y + (view.bottom - event.clientY) : rect.height
            });
          }}
          onMouseLeave={() => crosshair.clear()}
        >
          {tracks.map((track, index) => (
            <div
              key={track.key}
              className={styles.bodyCell}
              data-testid={`log-track-${track.key}`}
              style={{ width: track.width ?? 170 }}
            >
              <LogTrack
                chartRef={registerChart(track.key)}
                trackId={track.key}
                title={track.title}
                series={track.series}
                depthExtremes={extremes}
                onAfterSetExtremes={handleAfterSetExtremes}
                valueAxis={track.valueAxis}
                depthPlotBands={track.depthPlotBands}
                showDepthLabels={index === 0}
                showValueLabels={track.key !== 'depth'}
                height={trackHeight}
                boost={track.boost}
              />
            </div>
          ))}
          <CrosshairLine store={crosshair} extremes={extremes} plot={plot} />
          <CrosshairTooltip
            store={crosshair}
            groups={readoutGroups}
            depthLabel={depthReference.toUpperCase()}
            tolerance={depthTolerance}
          />
        </div>
      </div>
    </div>
  );
};

/**
 * Memoised because opening a parameter drawer re-renders the page, and this
 * subtree is four Highcharts instances.
 *
 * Every prop is reference-stable -- the arrays and objects come straight off
 * page state, the rest are primitives, and there are no function props -- so
 * the comparison genuinely bails out rather than always returning false. The
 * inner `useMemo`s would mostly hold on their own; this stops the whole
 * subtree at the boundary instead of relying on three of them to keep
 * holding as props change shape later.
 */
export const LogViewer = React.memo(LogViewerComponent);
LogViewer.displayName = 'LogViewer';

export default LogViewer;
